import type { Comparable, ComparableIdentity } from "./comparable";
import { useState } from "react";
import { Columns2, Plus, X } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useHasPermission } from "@/auth/access-permissions";
import { Button } from "@/components/design/Button";

export function ComparisonSelection<T extends ComparableIdentity>({ comparable, viewerId, current: currentItem }: { comparable: Comparable<T>; viewerId: string; current?: T }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const allowed = useHasPermission(comparable.permission);
  const selection = comparable.useSelection();
  const items = selection.viewerId === viewerId ? selection.items : [];
  const canAddCurrent = currentItem && !items.some((p) => p.id === currentItem.id);
  const intl = useIntl();
  const navigate = useNavigate();
  if (!allowed || items.length === 0) return null;
  return (
    <div id={`${comparable.kind}-comparison-selection`} className="flex min-w-0 flex-wrap items-center gap-2" aria-live="polite">
      <div className="smycles-chip-button flex min-w-0 max-w-full items-center divide-x divide-ink/10 rounded-full p-0">
        {items.map((p) => <div key={p.id} className="inline-flex min-w-0 items-center">
          {comparable.identity(p)}
          <Button id={`${comparable.kind}-comparison-remove-${p.id}`} variant="icon" size="sm" className="shrink-0" aria-label={intl.formatMessage({ id: comparable.messages.removeName }, { name: p.name })} onClick={() => selection.remove(p.id)}><X size={14} aria-hidden="true" /></Button>
        </div>)}
      </div>
      {items.length === 2 && <Button id={`${comparable.kind}-comparison-open`} size="compact" className="shrink-0 gap-2" onClick={() => navigate(`${comparable.path}?${new URLSearchParams({ left: items[0]!.id, right: items[1]!.id })}`)}><Columns2 size={16} aria-hidden="true" /><FormattedMessage id={comparable.messages.title} /></Button>}
      {items.length < 2 && <Button id={`${comparable.kind}-comparison-add`} variant="chip" size="compact" aria-label={intl.formatMessage({ id: canAddCurrent ? comparable.messages.select : comparable.messages.addItem })} onClick={() => {
          if (canAddCurrent) selection.select(viewerId, currentItem);
          else setPickerOpen(true);
        }}><Plus size={16} aria-hidden="true" /></Button>}
      {pickerOpen && items.length < 2 && comparable.picker({ chosenIds: items.map((p) => p.id), onClose: () => setPickerOpen(false), onPick: (item) => {
        selection.select(viewerId, item); setPickerOpen(false);
      } })}
    </div>
  );
}
